import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Minus, Plus, Trash2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

const CartPage = () => {
  const { cart, updateQuantity, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const subtotal = cart?.subtotal ?? cart?.total ?? 0;
  const promoDiscount = cart?.discount || 0;
  const promoPercent = cart?.promo_percent || 0;
  const discountedSubtotal = subtotal - promoDiscount;
  const tax = discountedSubtotal * 0.07;
  const estimatedShipping = discountedSubtotal >= 100 ? 0 : 14.05;
  const total = discountedSubtotal + tax + estimatedShipping;

  return (
    <div className="min-h-screen bg-[#050505] pt-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-heading text-5xl tracking-wide mb-8">YOUR CART</h1>

        {!cart?.items?.length ? (
          <div className="text-center py-16" data-testid="empty-cart">
            <ShoppingCart className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg mb-6">Your cart is empty</p>
            <Link to="/products" className="inline-block bg-[#D62828] hover:bg-[#B91C1C] text-white px-8 py-3 rounded-sm font-bold uppercase tracking-wider transition-all">Shop Now</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="cart-content">
            <div className="lg:col-span-2 space-y-4">
              {cart.items.map((item) => (
                <div key={item.product_id} className="bg-[#121212] border border-white/5 rounded-sm p-4 flex gap-4" data-testid={`cart-item-${item.product_id}`}>
                  <div className="w-24 h-24 bg-[#1A1A1A] rounded-sm overflow-hidden flex-shrink-0">
                    <img src={item.image_url || 'https://images.unsplash.com/photo-1692486407015-3a47e4c1181c'} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white mb-1">{item.name}</h3>
                    <p className="text-[#D62828] font-bold">${item.price.toFixed(2)}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="flex items-center gap-2">
                      <button onClick={() => updateQuantity(item.product_id, item.quantity - 1)} className="w-8 h-8 bg-[#1A1A1A] border border-white/10 rounded-sm flex items-center justify-center hover:border-white/30 transition-colors">
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-10 text-center">{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.product_id, item.quantity + 1)} className="w-8 h-8 bg-[#1A1A1A] border border-white/10 rounded-sm flex items-center justify-center hover:border-white/30 transition-colors">
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    <button onClick={() => updateQuantity(item.product_id, 0)} className="text-gray-500 hover:text-red-500 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              <button onClick={clearCart} className="text-gray-500 hover:text-white text-sm flex items-center gap-2 transition-colors">
                <Trash2 className="w-4 h-4" />Clear Cart
              </button>
            </div>

            <div className="bg-[#121212] border border-white/5 rounded-sm p-6 h-fit" data-testid="cart-summary">
              <h3 className="font-heading text-xl mb-6">ORDER SUMMARY</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between text-gray-400">
                  <span>Subtotal</span><span>${subtotal.toFixed(2)}</span>
                </div>
                {promoDiscount > 0 && (
                  <div className="flex justify-between text-green-400 font-semibold" data-testid="cart-promo-line">
                    <span>Grand Opening {promoPercent}% Off</span>
                    <span>-${promoDiscount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-400">
                  <span>Tax (7%)</span><span>${tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Shipping (Est.)</span>
                  <span>{estimatedShipping === 0 ? 'FREE' : `$${estimatedShipping.toFixed(2)}`}</span>
                </div>
                {discountedSubtotal < 100 && discountedSubtotal > 0 && (
                  <p className="text-xs text-green-500">Add ${(100 - discountedSubtotal).toFixed(2)} more for free shipping!</p>
                )}
                {discountedSubtotal >= 100 && <p className="text-xs text-green-500">You qualify for FREE shipping!</p>}
                <div className="border-t border-white/10 pt-3 flex justify-between text-lg font-bold">
                  <span>Total</span><span className="text-[#D62828]" data-testid="cart-total">${total.toFixed(2)}</span>
                </div>
              </div>

              {user ? (
                <button onClick={() => navigate('/checkout')} className="w-full mt-6 bg-[#D62828] hover:bg-[#B91C1C] text-white py-4 rounded-sm font-bold uppercase tracking-wider transition-all btn-glow" data-testid="checkout-btn">
                  Checkout
                </button>
              ) : (
                <div className="mt-6 space-y-3">
                  <Link to="/login" className="block w-full bg-[#D62828] hover:bg-[#B91C1C] text-white py-4 rounded-sm font-bold uppercase tracking-wider transition-all text-center">
                    Login to Checkout
                  </Link>
                  <Link to="/register" className="block w-full border border-white/20 hover:border-white/40 text-white py-4 rounded-sm font-bold uppercase tracking-wider transition-all text-center">
                    Create Account
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPage;
